import React, { useState } from "react";
import { DraftOrderDetail } from "../hooks/useDraft";
import { notifyWarning } from "src/assets/toastService";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";

type ApprovalAction = "Approve" | "increase-credit" | "Rejected";

interface ApprovalModalProps {
  draft: DraftOrderDetail;
  onClose: () => void;
  updatesLedgerLimit: (creditLimit: number) => void;
  draftStatusUpdate: (status: string) => void;
}

export default function ApprovalModal({ draft, onClose, updatesLedgerLimit, draftStatusUpdate }: ApprovalModalProps) {
  const [action, setAction] = useState<ApprovalAction | null>(null);
  const [creditLimit, setCreditLimit] = useState("");
  const [denyReason, setDenyReason] = useState("");

  const handleConfirm = async () => {
    if (!creditLimit || Number(creditLimit) <= 0) {
      notifyWarning("Enter valid Credit Limit");
      return;
    }
    updatesLedgerLimit(Number(creditLimit));
  };

  return (
    <ModalWrapper title={`Review order ${draft.orderId}`} onClose={onClose} width="max-w-md">
      <div className="p-2">
        <p className="text-sm text-[#5B6472] mb-4">Choose an action for this order.</p>

        {!action && (
          <div className="space-y-2">
            <ActionOption label="Approve this order" description="Confirms the order as-is." onClick={() => draftStatusUpdate("Accepted")} />
            <ActionOption label="Increase credit limit" description="Raise the customer's credit limit, then approve." onClick={() => setAction("increase-credit")} />
            <ActionOption label="Deny this order" description="Rejects the order." onClick={() => draftStatusUpdate("Rejected")} tone="danger" />
          </div>
        )}

        {action === "increase-credit" && (
          <label className="block text-sm text-[#5B6472]">
            New credit limit
            <input
              type="number"
              autoFocus
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              placeholder={`Current outstanding: ${draft.outstandingBalance}`}
              className="mt-1 w-full border border-[#E3E6EB] rounded-md px-3 py-2 text-sm text-[#14181F] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#2B4AE0]"
            />
          </label>
        )}

        {action === "Rejected" && (
          <label className="block text-sm text-[#5B6472]">
            Reason (optional)
            <textarea
              autoFocus
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-[#E3E6EB] rounded-md px-3 py-2 text-sm text-[#14181F] focus:outline-none focus:ring-2 focus:ring-[#2B4AE0]"
            />
          </label>
        )}

        {/* ✅ Sub-action buttons — Back + Confirm */}
        {action && (
          <div className="flex justify-end gap-2 mt-5">
            <CommonButtons label="Back" variant="outlineDanger" onClick={() => setAction(null)} className="px-6 py-2" />
            <CommonButtons label={action === "Rejected" ? "Confirm deny" : "Confirm"} variant={action === "Rejected" ? "danger" : "primary"} onClick={handleConfirm} className="px-4 py-2" />
          </div>
        )}

        {/* ✅ Cancel */}
        {!action && <CommonButtons label="Cancel" variant="outlineDanger" onClick={onClose} className="mt-4" />}
      </div>
    </ModalWrapper>
  );
}

function ActionOption({ label, description, onClick, tone = "default" }: { label: string; description: string; onClick: () => void; tone?: "default" | "danger" }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left border rounded-md px-3 py-2.5 transition-colors ${tone === "danger" ? "border-[#F4D3D0] hover:bg-[#FCEAE9]" : "border-[#E3E6EB] hover:bg-[#F6F7F9]"}`}
    >
      <div className={`text-sm font-medium ${tone === "danger" ? "text-[#B3261E]" : "text-[#14181F]"}`}>{label}</div>
      <div className="text-xs text-[#5B6472]">{description}</div>
    </button>
  );
}
